import { ChangeEvent, useCallback, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Scope } from '@unform/core'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import Table from '~/components/Table'
import { Badge } from '~/components/UI/Badge'
import { FileInput, InputHidden, Label } from '~/components/unform'
import { getWhiteLabelPublisherId } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { listSizesBySegmentationService } from '~/modules/retailMedia/services/placements/sizes'

import FileToUpload from '../FileToUpload'
import { schema } from './schema'

import * as S from './styles'

export const MAX_FILE_SIZE_MB = 50 // 50MB
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024 // 50MB in bytes
export const MIN_DURATION_SECONDS = 10 // 10 seconds

const deriveOrientation = (width: number, height: number): Orientation => {
  if (width === height) return 'square'
  return width > height ? 'landscape' : 'portrait'
}

export const Video = ({
  formRef,
  onError,
  setRequiredData
}: VideoUploadProps) => {
  const { t } = useAPPTranslation()
  const [uploadedMediaToTable, setUploadedMediaToTable] = useState<
    UploadedMediaTableItem[]
  >([])

  /**
   * Separando arquivos para inserir na fila de upload
   */
  type FilesToUpload = { file: File; id: string }[]
  const [filesToUpload, setFilesToUpload] = useState<FilesToUpload>([])

  /**
   * Lógica de apoio
   */

  const rmid = useQueryParams().get('rmid') || getWhiteLabelPublisherId()

  const { data: sizes = [] } = useQuery({
    queryKey: ['get-placement-sizes-digital-signage', rmid],
    queryFn: async () => {
      const res = await listSizesBySegmentationService({
        type: 'digital_signage',
        publisher_id: rmid
      })
      return res.data ?? []
    },
    enabled: !!rmid
  })

  /**
   * Handle section is valid
   * * Refactor
   */

  const sectionIsValid = useMemo(() => {
    setRequiredData((prev: RequiredDataToSubmit) => ({
      ...prev,
      isMediaValid: !!uploadedMediaToTable?.length,
      uploadedMediaToTable
    }))

    return !!uploadedMediaToTable?.length
  }, [uploadedMediaToTable])

  /**
   * Handle props panel
   */
  const handlePrepareToUploadImage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(
        event.target.files as FileList,
        (item, index) => ({ file: item, id: `${item.name}_${index}` })
      )

      setFilesToUpload(fileList)
    },
    []
  )

  const handleFileError = useCallback(
    (hasError: boolean) => {
      onError(hasError)
    },
    [onError]
  )

  const panelProps = useMemo(
    () => ({
      title: t('rm:campaign.create.Video.title'),
      iconLeft: <BadgeIsValid isValid={sectionIsValid} />,
      isCollapsible: true
    }),
    [sectionIsValid, t]
  )

  /**
   * Handle props file input
   */
  const fileInputProps: FileInputProps = {
    name: 'mediaFile',
    accept: 'video/mp4,video/x-m4v,video/*',
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      handlePrepareToUploadImage(event),
    hideIcons: true
  }

  /**
   * Available media configs
   * Formatando as configurações de mídia disponíveis recebidas da API
   */

  const availableMediaConfigs = useMemo(() => {
    if (sizes) {
      const arrayValue: AvailableMediaConfigs[] = sizes.map(item => {
        const orientation: Orientation =
          item.orientation ?? deriveOrientation(item.width, item.height)
        return {
          value: item.name,
          label: item.size,
          width: item.width,
          height: item.height,
          duration: item.duration,
          orientation
        }
      })

      // Separando os itens pela orientação
      const arrayByOrientation = arrayValue.reduce(
        (acc, item) => {
          if (!acc[item.orientation]) {
            acc[item.orientation] = []
          }
          acc[item.orientation].push(item)
          return acc
        },
        {} as Record<Orientation, AvailableMediaConfigs[]>
      )

      // Ordenando os tamanhos por largura e altura
      Object.keys(arrayByOrientation).forEach(key => {
        const orientation = key as Orientation
        arrayByOrientation[orientation].sort((a, b) => {
          if (a.width !== b.width) {
            return a.width - b.width
          }
          return a.height - b.height
        })
      })

      return arrayByOrientation
    }
  }, [sizes])

  /**
   * Lidando com ações de upload
   */
  const handleDeleteMedia = useCallback(
    (data: ItemMediaTable) => {
      const filterState = (state: UploadedMediaTableItem[]) =>
        state.filter(item => item.id !== data.id)

      setUploadedMediaToTable(filterState)
    },
    [setUploadedMediaToTable]
  )

  const getPlacementByDimension = useCallback(
    ({ width, height }: VideoProps) => {
      const findPlacement = sizes?.find(
        item => Number(item.width) === width && Number(item.height) === height
      )

      return findPlacement
    },
    [sizes]
  )

  const handleSaveMedia = useCallback(
    (data: ItemMediaTable) => {
      const altText = formRef.current.getFieldValue('video.alt_text')

      setFilesToUpload(prevState =>
        prevState.filter(item => item.id !== data.id)
      )

      setUploadedMediaToTable(prevState => {
        return [...prevState, { ...data, alt_text: altText, handleDeleteMedia }]
      })
    },
    [formRef, handleDeleteMedia, setUploadedMediaToTable]
  )

  const handleDeleteFileBeforeUpload = (removeId: string) => {
    setFilesToUpload(prevState =>
      prevState.filter(item => item.id !== removeId)
    )
  }

  return (
    <Panel {...panelProps}>
      <Scope path="video">
        {!filesToUpload?.length && !uploadedMediaToTable.length && (
          <>
            <header>
              <Label
                text={t('rm:campaign.create.Video.label')}
                helperText={t('rm:campaign.create.Video.helperText', {
                  size: MAX_FILE_SIZE_MB
                })}
              />
            </header>
            <section className="wrapper-file-input">
              <FileInput {...fileInputProps}>
                <div className="pt-3 mt-3 border-top border-tertiary">
                  <h4>{t('rm:allowedMediaSizes')}</h4>

                  <div className="d-flex gap 2 align-items-center flex-column">
                    {availableMediaConfigs &&
                      Object.entries(availableMediaConfigs).map(
                        ([orientation, sizes]) => (
                          <div
                            key={orientation}
                            className="d-flex flex-row align-items-center"
                          >
                            <Badge
                              template="primary"
                              text={t(
                                `rm:campaign.create.Video.${orientation}`
                              )}
                            />
                            {sizes?.map(item => (
                              <div key={item.value}>
                                <Badge>{item.label}</Badge>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                  </div>

                  <S.Definitions>
                    <p>
                      <strong>
                        {t('rm:campaign.create.Video.maximumSize')}
                      </strong>
                      {MAX_FILE_SIZE_MB}MB
                    </p>
                    <p>
                      <strong>
                        {t('rm:campaign.create.Video.minimumDuration')}
                      </strong>
                      {MIN_DURATION_SECONDS} {t('rm:seconds')}
                    </p>
                  </S.Definitions>
                </div>
              </FileInput>
            </section>
          </>
        )}

        <section className="wrapper-video-to-upload d-flex flex-column">
          {filesToUpload?.map(item => (
            <FileToUpload
              data={item}
              key={item.id}
              getPlacementByDimension={getPlacementByDimension}
              onSave={handleSaveMedia}
              onDelete={() => handleDeleteFileBeforeUpload(item.id)}
              onError={handleFileError}
            />
          ))}
        </section>

        {!!uploadedMediaToTable.length && (
          <>
            <S.WrapperTabler>
              <Table schema={schema} data={uploadedMediaToTable} />
            </S.WrapperTabler>

            <video controls style={{ maxHeight: '400px', width: '100%' }}>
              <source
                src={uploadedMediaToTable[0].media_url}
                type="video/mp4"
              />
            </video>

            {uploadedMediaToTable.map((item, index) => (
              <Scope path={`assets[${index}]`} key={item.id}>
                <InputHidden
                  type="hidden"
                  name={`media_url`}
                  value={item.media_url}
                />
                <InputHidden
                  type="hidden"
                  name={`ad_size`}
                  value={item.ad_size}
                />
                <InputHidden
                  type="hidden"
                  name={`alt_text`}
                  value={item.alt_text}
                />
                <InputHidden
                  type="hidden"
                  name={`duration`}
                  value={item.dimensions.duration}
                />
                <InputHidden
                  type="hidden"
                  name={`width`}
                  value={item.dimensions.width}
                />
                <InputHidden
                  type="hidden"
                  name={`height`}
                  value={item.dimensions.height}
                />
                <InputHidden
                  type="hidden"
                  name={`file_size`}
                  value={item.file_size}
                />
              </Scope>
            ))}
          </>
        )}
      </Scope>
    </Panel>
  )
}
